/**
 * Onboarding state: the setup wizard, the first-plan tour and the starter checklist (docs/PLAN-onboarding.md).
 *
 * Everything here is local to this browser / desktop app (localStorage `mcp-onboarding-v1`) — nothing is sent
 * anywhere. People who used MC Planner before the wizard existed (they dismissed the old welcome card,
 * `mcp-welcome-v1`) count as returning users: no welcome screen, no tour, checklist hidden.
 */
import { create } from "zustand";
import { detectOS, type Launcher, type OS } from "./launchers.ts";

export type Goal = "path" | "town" | "build" | "explore";
export type WizardStep = "boot" | "welcome" | "launcher" | "textures" | "world" | "home";
export type ChecklistItem = "draw" | "layout" | "schematic" | "board" | "together";

export type LastWorld =
  | { kind: "local"; path: string; name: string }
  | { kind: "recent"; key: string; name: string }
  | { kind: "demo"; name: string };

export const CHECKLIST: { id: ChecklistItem; label: string; hint: string }[] = [
  { id: "draw", label: "Draw a plan", hint: "Pick Path, Area or Building on the left (keys 2–4) and click on the map." },
  { id: "layout", label: "Fill an area with a layout", hint: "Select an area, then “Fill with a layout…”: streets, plots and houses in one go." },
  { id: "schematic", label: "Place a schematic", hint: "Import a .litematic / .schem file (key 7) and place the real build on your map." },
  { id: "board", label: "Pin an idea to a board", hint: "Select anything, then Pinboard in its panel: paste screenshots, links, notes and checklists." },
  { id: "together", label: "Plan with friends", hint: "Collaborate (top right), then share the world and send the invite link." },
];

interface Persisted {
  /** passed the welcome screen once */
  welcomed: boolean;
  launcher: Launcher | null;
  os: OS;
  /** which first plan the tour teaches (null = a road). Asked while the world loaded until 1 Nov 2026; kept for those who answered */
  goal: Goal | null;
  /** first-plan tour finished or skipped (false = still a new user) */
  tourDone: boolean;
  checklist: Partial<Record<ChecklistItem, boolean>>;
  checklistHidden: boolean;
  lastWorld: LastWorld | null;
}

export interface PendingWorld {
  name: string;
  zip?: File;
  files?: { path: string; file: Blob }[];
}

interface OnboardingState extends Persisted {
  step: WizardStep;
  /** a new user's loading screen stays until the world is drawn (then they go in by themselves: fly-in + tour) */
  holding: boolean;
  /** the fly-in + tour should start once the world is on screen */
  landing: boolean;
  /** active tour step (null = no tour running) */
  tourStep: number | null;
  /** the object drawn during the tour */
  tourObject: string | null;
  /** a world dropped before the textures were ready: opened right after them */
  pendingWorld: PendingWorld | null;
  /** short, friendly message on the current wizard card (wrong file, …) */
  note: { text: string; tone: "info" | "warn" } | null;
  /** resource packs / mod jars to layer on top of the game jar */
  extras: File[];
  /** the world being opened (for the loading screen: its launcher profile, when known) */
  opening: { name: string; profile?: string } | null;
  /** "Back to worlds" was pressed while the world was still being read: close it as soon as it's on screen */
  leaving: boolean;
  set: (p: Partial<OnboardingState>) => void;
}

const KEY = "mcp-onboarding-v1";

function load(): Persisted {
  const fresh: Persisted = { welcomed: false, launcher: null, os: detectOS(), goal: null, tourDone: false, checklist: {}, checklistHidden: false, lastWorld: null };
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) return { ...fresh, ...(JSON.parse(raw) as Partial<Persisted>) };
    // used the app before the wizard: a returning user
    if (localStorage.getItem("mcp-welcome-v1") === "1") return { ...fresh, welcomed: true, tourDone: true, checklistHidden: true };
  } catch { /* storage blocked: behave like a new user for this visit */ }
  return fresh;
}

export const useOnboarding = create<OnboardingState>((set) => ({
  ...load(),
  step: "boot",
  holding: false,
  landing: false,
  tourStep: null,
  tourObject: null,
  pendingWorld: null,
  note: null,
  extras: [],
  opening: null,
  leaving: false,
  set: (p) => set(p),
}));

useOnboarding.subscribe((s) => {
  const keep: Persisted = { welcomed: s.welcomed, launcher: s.launcher, os: s.os, goal: s.goal, tourDone: s.tourDone, checklist: s.checklist, checklistHidden: s.checklistHidden, lastWorld: s.lastWorld };
  try { localStorage.setItem(KEY, JSON.stringify(keep)); } catch { /* ignore */ }
});

/** still a new user (the first-plan tour hasn't been finished or skipped) */
export const isNewUser = () => !useOnboarding.getState().tourDone;

/** Mark a starter-checklist item as done (called by the controller when it happens). */
export function track(item: ChecklistItem) {
  const s = useOnboarding.getState();
  if (s.checklist[item]) return;
  s.set({ checklist: { ...s.checklist, [item]: true } });
}

/** "1 095": counts with a narrow no-break space between thousands (the design's number style) */
export function fmtCount(n: number) {
  return Math.round(n).toLocaleString("en").replace(/,/g, " ");
}

/** The next unticked starter-checklist item (null when all are done). */
export function nextChecklistItem(done: Partial<Record<ChecklistItem, boolean>>) {
  return CHECKLIST.find((i) => !done[i.id]) ?? null;
}

export function prefersReducedMotion() {
  try { return matchMedia("(prefers-reduced-motion: reduce)").matches; } catch { return false; }
}

/** Start the first-plan tour again (Help → Replay the tour). */
export function replayTour() {
  useOnboarding.getState().set({ tourStep: 0, tourObject: null, tourDone: false });
}
